import { useEffect, useMemo, useRef, useState } from 'react'

import type { UseStepperTiersProps } from '../types/types'

export const useStepperTiers = ({
	value,
	steps,
	max,
}: UseStepperTiersProps) => {
	const stepsContainerRef = useRef<HTMLDivElement>(null)
	const [stepWidth, setStepWidth] = useState<number>()
	const [normalizedValue, setNormalizedValue] = useState<number>(0)
	const currentStep = useMemo(
		() => steps.findLastIndex(({ threshold }) => value >= threshold),
		[value, steps]
	)

	useEffect(() => {
		if (stepsContainerRef.current) {
			const widthPerStep = stepsContainerRef.current.clientWidth / steps.length
			setStepWidth(widthPerStep)
		}
	}, [stepsContainerRef])

	useEffect(() => {
		if (steps.length <= 1) {
			return
		}

		if (value <= 0) {
			setNormalizedValue(0)
			return
		}

		if (value >= max) {
			setNormalizedValue(max)
			return
		}

		const stepProportion = 1 / (steps.length - 1)

		const mappedThresholdToStepProportions = steps.map(
			({ threshold }, index) => {
				const prevThreshold = steps[index - 1]?.threshold ?? 0

				return (threshold - prevThreshold) / max
			}
		)

		const baseValue = max * stepProportion * currentStep
		const extraValue = value - steps[currentStep].threshold
		const thresholdProportion =
			stepProportion / mappedThresholdToStepProportions[currentStep + 1]

		const newValue = baseValue + extraValue * thresholdProportion
		setNormalizedValue(newValue)
	}, [value, steps, currentStep, stepsContainerRef])

	return {
		normalizedValue,
		currentStep,
		stepWidth,
		stepsContainerRef,
	}
}
